import React from "react";
import clsx from "clsx";
import { useNavigate } from "react-router";
import { IconChevronDown } from "@tabler/icons-react";
import SubMenu, { SubMenuProps } from "./SubMenu";
import useBuildingMenu from "./useBuildingMenu";
import ReactDOM from "react-dom";
import useOutsideClick from "hooks/useOutsideClick";

export interface MenuItemProps {
  name: string;
  path?: string;
  icon?: React.ReactNode;
  subtitle?: string;
  subMenu?: SubMenuProps[];
}

const MenuItem = (props: { item: MenuItemProps }) => {
  const navigate = useNavigate();
  const [openSubMenu, setOpenSubMenu] = React.useState(false);
  const { isMenuSelected } = useBuildingMenu();
  const isSelected = isMenuSelected(props.item);
  const menuItemRef = React.useRef<HTMLDivElement>(null);
  const subMenuRef = React.useRef<HTMLDivElement>(null);
  const closeSubMenu = () => setOpenSubMenu(false);

  useOutsideClick(menuItemRef, closeSubMenu, subMenuRef);

  const handleMenuClicked = () => {
    props.item.path
      ? navigate(props.item.path, { replace: true })
      : setOpenSubMenu(!openSubMenu);
  };

  React.useEffect(() => {
    addEventListener("scroll", closeSubMenu);
    return () => removeEventListener("scroll", closeSubMenu);
  });

  return (
    <div className="px-1 first-of-type:pl-0 last-of-type:pr-0">
      <div
        className={clsx(
          "flex items-center p-2 hover:bg-indigo-50 cursor-pointer group/menuItem rounded-md relative transition",
          "text-sm font-medium whitespace-nowrap h-10",
          { "bg-indigo-50": openSubMenu || isSelected }
        )}
        ref={menuItemRef}
        onClick={handleMenuClicked}
      >
        <span
          className={clsx(
            "text-gray-500 group-hover/menuItem:text-indigo-500 transition",
            {
              "text-indigo-500": openSubMenu || isSelected,
            }
          )}
        >
          {props.item.icon}
        </span>
        <span
          className={clsx(
            "mx-1 text-gray-700 group-hover/menuItem:text-indigo-700 transition",
            { "text-indigo-700": openSubMenu || isSelected }
          )}
        >
          {props.item.name}
        </span>
        {props.item.subMenu?.length && (
          <IconChevronDown
            className={clsx(
              "ml-1 group-hover/menuItem:text-indigo-700 transition",
              {
                "text-indigo-700": openSubMenu || isSelected,
              }
            )}
          />
        )}
        {openSubMenu &&
          props.item.subMenu &&
          ReactDOM.createPortal(
            <SubMenu
              menu={props.item.subMenu}
              ref={subMenuRef}
              xPos={
                Number(menuItemRef.current?.getBoundingClientRect().left) +
                Number(menuItemRef.current?.clientWidth) / 2
              }
            />,
            document.body
          )}
      </div>
    </div>
  );
};

export default MenuItem;
